"use client";

import { useEffect, useState } from "react";

import { Button } from "@artha/ui";

import { PageLayout } from "@/components/layout/PageLayout";
import { CreatorDetailHeader } from "@/components/layout/CreatorDetailHeader";
import {
  CREATOR_DETAIL_MAIN_CLASS_NAME,
  CREATOR_DETAIL_PAGE_CLASS_NAME,
  CREATOR_DETAIL_SECTION_CLASS_NAME,
  CREATOR_FLOATING_ACTIONS_CONTENT_CLASS_NAME,
} from "@/components/layout/creatorDetail.constants";
import { getMediaAssetUrl } from "@/lib/utils";

import { useCandidatePersonalization } from "../hooks/useCandidatePersonalization";

import { CandidateProfileSkeleton } from "./CandidateProfileSkeleton";
import { PROFILE_HEADER_EMOJI_PATTERN } from "../personalization.constants";
import { fetchCandidateSession } from "../services/personalizationClient";
import { fetchTalentNetworkSession } from "../../talent-network/services/talentNetworkClient";

import { readCandidateProfileDraft } from "../services/profileDraft";

import type {
  CandidateProfilePageProps,
  CandidateProfileReviewDraft,
  CandidateProfileSession,
} from "../personalization.types";

import { CandidateProfileForm } from "./CandidateProfileForm";
export function CandidateProfilePage({
  username,
  creator,
  source,
}: CandidateProfilePageProps) {
  const controller = useCandidatePersonalization(creator.id);
  const isTalentNetwork = source === "talent_network";
  const profileCreatorName =
    creator.displayName?.replace(PROFILE_HEADER_EMOJI_PATTERN, "").trim() ||
    username;
  const { isSessionLoading, restoreProfileDraft } = controller;
  const [draft, setDraft] = useState<CandidateProfileReviewDraft | null>(null);
  const [session, setSession] = useState<CandidateProfileSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (isSessionLoading) return;
    let active = true;
    void (
      source === "talent_network"
        ? fetchTalentNetworkSession(username)
        : fetchCandidateSession()
    )
      .then((session) => {
        if (!active) return;
        setSession(session);
        const saved = readCandidateProfileDraft(username, source);
        const current =
          saved &&
          (!session.authenticated ||
            saved.externalId === session.externalId ||
            saved.ownerExternalId === session.externalId)
            ? saved
            : session.authenticated &&
                session.externalId &&
                session.editableProfile
              ? {
                  externalId: session.externalId,
                  editableProfile: session.editableProfile,
                }
              : null;
        if (current) {
          restoreProfileDraft(current);
        }
        setDraft(current);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [attempt, isSessionLoading, restoreProfileDraft, source, username]);

  return (
    <PageLayout className={CREATOR_DETAIL_PAGE_CLASS_NAME}>
      <main className={CREATOR_DETAIL_MAIN_CLASS_NAME}>
        <section className={CREATOR_DETAIL_SECTION_CLASS_NAME}>
          <CreatorDetailHeader
            username={username}
            creatorName={creator.displayName}
            title={`${profileCreatorName}'s talent network`}
            creatorAvatarUrl={getMediaAssetUrl(creator.avatarUrl)}
            backHref={`/@${encodeURIComponent(username)}${isTalentNetwork ? "/talent-network" : ""}`}
            backLabel={
              isTalentNetwork ? "Back to Talent Network" : "Back to jobs"
            }
          />
          <div className={CREATOR_FLOATING_ACTIONS_CONTENT_CLASS_NAME}>
            {loading ? (
              <CandidateProfileSkeleton />
            ) : error ? (
              <>
                <p role="alert">
                  Unable to load your profile. Please try again.
                </p>
                <Button
                  className="mt-4 w-full"
                  onClick={() => {
                    setLoading(true);
                    setError(false);
                    setAttempt((value) => value + 1);
                  }}
                >
                  Try again
                </Button>
              </>
            ) : draft ? (
              <CandidateProfileForm
                key={`${source}:${username}`}
                username={username}
                creatorName={profileCreatorName}
                source={source}
                controller={controller}
                draft={draft}
                session={session}
              />
            ) : (
              <p>
                {isTalentNetwork
                  ? "Join the Talent Network and upload your resume to review your profile."
                  : "Use Find relevant jobs on the Jobs page to upload your resume and review your profile."}
              </p>
            )}
          </div>
        </section>
      </main>
    </PageLayout>
  );
}
